// Icon: typed wrapper around lucide-react.
// Renders nothing gracefully if the icon name is unknown (warns in dev).

import type { FC, ReactElement } from 'react';
import { icons, type LucideProps } from 'lucide-react';

export interface IconProps extends Omit<LucideProps, 'ref'> {
  /** Lucide icon name in kebab-case, e.g. "arrow-up-right". */
  name: string;
}

/** Convert kebab-case to PascalCase ("arrow-up-right" → "ArrowUpRight"). */
function toPascalCase(s: string): string {
  return s
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

export function Icon({ name, size = 20, ...rest }: IconProps): ReactElement | null {
  const lookupKey = toPascalCase(name);
  const Component = (icons as Record<string, FC<LucideProps>>)[lookupKey];

  if (!Component) {
    if (import.meta.env.DEV) {
      console.warn(`[Icon] unknown lucide icon: "${name}" (looked up "${lookupKey}")`);
    }
    return null;
  }

  return <Component size={size} aria-hidden="true" {...rest} />;
}
